import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { defaultAspect, getCachedAspect } from "./usePhotoMonths";

const slideVariants = {
  enter: (direction) => ({ x: direction >= 0 ? 220 : -220, opacity: 0, scale: 0.94, rotate: direction * 2 }),
  center: { x: 0, opacity: 1, scale: 1, rotate: 0 },
  exit: (direction) => ({ x: direction >= 0 ? -220 : 220, opacity: 0, scale: 0.94, rotate: -direction * 2 }),
};

const swipeThreshold = 60;

function LightboxPhoto({ src, alt }) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [aspect, setAspect] = useState(() => getCachedAspect(src) || defaultAspect);
  return (
    <div className="lightbox-photo" style={{ "--aspect": aspect }}>
      <img
        src={src}
        alt={alt}
        decoding="async"
        draggable={false}
        className={isLoaded ? "is-loaded" : undefined}
        onLoad={(event) => {
          const { naturalWidth, naturalHeight } = event.currentTarget;
          if (naturalWidth && naturalHeight) setAspect(naturalWidth / naturalHeight);
          setIsLoaded(true);
        }}
      />
    </div>
  );
}

function ArrowIcon({ isNext }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d={isNext ? "M9 5l7 7-7 7" : "M15 5l-7 7 7 7"}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Lightbox({ photos, index, direction, onNavigate, onClose }) {
  const hasSiblings = photos.length > 1;
  const stopPropagation = (event) => event.stopPropagation();

  return (
    <motion.div
      className="lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={`photo ${index + 1} of ${photos.length}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={onClose}
    >
      <button type="button" className="lightbox-close" aria-label="close" onClick={onClose}>
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 6l12 12M18 6L6 18" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </button>

      <motion.div
        className="lightbox-stage"
        initial={{ scale: 0.92, y: 16 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.94, y: 10 }}
        transition={{ type: "spring", stiffness: 220, damping: 24 }}
      >
        <AnimatePresence initial={false} custom={direction}>
          <motion.div
            key={index}
            className="lightbox-frame"
            custom={direction}
            variants={slideVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: "spring", stiffness: 260, damping: 30, opacity: { duration: 0.2 } }}
            drag={hasSiblings ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.7}
            onDragEnd={(event, info) => {
              if (info.offset.x < -swipeThreshold || info.velocity.x < -500) onNavigate(1);
              else if (info.offset.x > swipeThreshold || info.velocity.x > 500) onNavigate(-1);
            }}
            onClick={stopPropagation}
          >
            <LightboxPhoto src={photos[index]} alt={`memory ${index + 1}`} />
          </motion.div>
        </AnimatePresence>
      </motion.div>

      {hasSiblings && (
        <div className="lightbox-nav">
          <button
            type="button"
            className="lightbox-arrow lightbox-arrow--previous"
            aria-label="previous photo"
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(-1);
            }}
          >
            <ArrowIcon isNext={false} />
          </button>
          <div className="lightbox-count" onClick={stopPropagation}>
            {index + 1} / {photos.length}
          </div>
          <button
            type="button"
            className="lightbox-arrow lightbox-arrow--next"
            aria-label="next photo"
            onClick={(event) => {
              event.stopPropagation();
              onNavigate(1);
            }}
          >
            <ArrowIcon isNext />
          </button>
        </div>
      )}
    </motion.div>
  );
}
